import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
  NgZone
} from '@angular/core';
import {Post, Sponsor} from '../../../../models/post-overview';
import {languageId, mentionType, systemValues} from '../../../app.constants';
import {InfluencerPostService} from '../../../services/influencer-post/influencer-post.service';
import {TranslateService} from '@ngx-translate/core';
import * as Chains from 'c4s';
import * as Sentry from '@sentry/browser';
import {AchooPopupComponent} from '../../../core/achoo-popup/achoo-popup.component';
import {ModalService} from '../../../services/modal/modal.service';
import {MentionService} from '../../../services/mention/mention.service';
import {PdfExportService} from '../../../services/export-pdf/pdf-export.service';
import {
  ActionDetails,
  ActionSource,
  ActivePages,
  EventTrackerService,
  Page,
  TargetType,
  UserActionType
} from '../../../services/event-tracker/event-tracker.service';
import {UserService} from '../../../services/user.service';

declare var $: any;

@Component({
  selector: 'post-content',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './post-content.component.html',
  styleUrls: ['./post-content.component.scss']
})
export class PostContentComponent implements AfterViewInit {
  static COMPONENT_NAME = 'PostContentComponent';
  @ViewChild('RequestErrorNotice')
  requestErrorNotice: AchooPopupComponent;
  @ViewChild('NotificationDeleteMentionError') notificationDeleteMentionError: AchooPopupComponent;
  @ViewChild('storyPreviewPopup') storyPreviewPopup: ElementRef;

  @Input()
  public postContent: Post;
  @Input()
  public postMentions: string[];
  @Input()
  public postSponsors: Sponsor[];
  @Input()
  public platformIconUrl: string | null;
  @Input()
  public _newMention: any| null;
  @Output()
  public refreshMentionList = new EventEmitter<any>();

  public showAddNewMention: boolean | false;

  public requestErrorMessage: string | null;

  constructor(private influencerPostService: InfluencerPostService,
              private mentionService: MentionService,
              private translate: TranslateService,
              private pdfService: PdfExportService,
              private userService: UserService,
              private eventTracker: EventTrackerService,
              private modalService: ModalService,
              private zone: NgZone) {}

  set newMention(value: any) {
    this._newMention = value;
  }

  get newMention() {
    return this._newMention;
  }

  getVideoLink(post: Post) {
    return post.isStory ? post.url : post.video;
  }

  public onLoadPostImageError() {
    this.postContent.image = systemValues.noImagePicture;
    this.onLoadedPostImage();
  }

  public onAddNewMention() {
    this.showAddNewMention = true;
  }
  public offAddNewMention() {
    this.showAddNewMention = false;
  }

  public onCloseRequestErrorNotice() {
    this.modalService.close();
  }

  public saveNewMention(evt: any) {
    const value = evt.value;

    // Stop processing if no input
    if (!value) {
      return;
    }

    // Update UI immediately
    if (Array.isArray(this.postMentions)) {
      this.postMentions.push(value);
    } else {
      this.postMentions = [value];
    }

    this.showAddNewMention = false;
    evt.value = '';

    // Prepare input for chained requests
    const state = {
      influencerPostService: this.influencerPostService,
      mention: value,
      keywordType: mentionType.keyword,
      languages: [languageId.global],
      postData: {
        topicId: this.postContent.topicId || 14,
        industryId: 28, // Default to 'Other' // TODO: this is a temporary hack until we replace the current topic system with stream aggr
        url: this.postContent.url,
        publishDate: this.postContent.publishedDate,
        relativePath: this.postContent.relativePath,
        urlHash: this.postContent.urlHash,
        influencerId: this.postContent.influencerId
      },
      eventTracker: this.eventTracker,
      postTitle: this.postContent.title,
      postUrlHash: this.postContent.urlHash,
    };

    // Run requests sequentially
    Chains(state)
      .then(this.checkPostMentionExist)
      .then(this.addNewMention)
      .then(this.approveNewMention)
      .then(this.addNewPostMentions)
      .catch((error) => {
        console.log(error);

        let serverErrorMsg;
        // if this error is not interal server error, show specific server error message
        if (error.status !== 500 && error.error && error.error.message) {
          serverErrorMsg = error.error.message;
        }

        // Revert back to previous state on error
        const mentionIndex = this.postMentions.findIndex(mention => mention === value);
        this.postMentions.splice(mentionIndex, 1);
        evt.value = value;
        this.showAddNewMention = true;

        // Report to Sentry
        Sentry.captureException(error);

        // Show user friendly error message
        this.handleRequestError({
          message: serverErrorMsg || this.translate.instant('page.post_overview.mention_error')
        });
      });
  }

  private handleRequestError(error: Response | any) {
    this.requestErrorMessage = error.message;
    this.modalService.open(this.requestErrorNotice);
  }

  // Chain handlers

  // Check if a mention exists
  private checkPostMentionExist(state, next) {
    state.influencerPostService.checkPostMentionExist(state.mention).subscribe(
      // Success
      (data) => {
        state.mention_exists = !!data.id;
        state.mention_id = data.id;

        next();
      },

      // Error
      (error: Response | any) => {
        next(error);
      }
    );
  }

  // Create a new mention
  private addNewMention(state, next) {
    // Mention already exists, skip this
    if (state.mention_exists) {
      return next();
    }

    // Validate input
    if (!state.mention || !state.keywordType || !state.languages || !state.postData || !state.postData.topicId || !state.postData.industryId) {
      return next(new Error('Missing parameters for `addNewMention()`.'));
    }

    // Make API call
    state.influencerPostService.addNewMention(state.mention, state.keywordType, state.languages, state.postData.topicId, state.postData.industryId).subscribe(
      // Success
      (data) => {
        if (!data) {
          return next(new Error('An error occurred while adding a new mention, `data` was undefined.'));
        }

        // Save new mention ID to state
        state.mention_id = data.id;

        const mentionParams = {
          keyword: state.mention,
          keywordType: state.keywordType,
          languages: state.languages,
          topic_id: state.postData.topicId,
          vertical_id: state.postData.industryId,
        };
        // keep track event to Segment
        state.eventTracker.trackUserAction(UserActionType.ACTION, Page.MENTION, {
          id: data.id,
          name: data.keyword,
          type: data.keywordType && data.keywordType.name
        }, {
          action: UserActionType.CREATE,
          source: ActionSource.PAGE,
          mentionParams
        }, undefined, {
          type: ActivePages.POST,
          name: state.postTitle,
          id: state.postUrlHash
        });

        next();
      },

      // Error
      (error: Response | any) => {
        next(error);
      }
    );
  }

  // Approve a newly created mention
  private approveNewMention(state, next) {
    // Mention had already been created, skip this
    if (state.mention_exists) {
      return next();
    }

    // Validate input
    if (!state.mention_id || !state.languages || !state.postData || !state.postData.topicId || !state.postData.industryId) {
      return next(new Error('Missing parameters for `approveNewMention()`.'));
    }

    // Make the API call
    state.influencerPostService.approveMention(state.mention_id, state.languages, state.postData.topicId, state.postData.industryId).subscribe(
      // Success
      (data) => {
        if (!data) {
          return next(new Error('An error occurred while approving a new mention, `data` was undefined.'));
        }

        next();
      },

      // Error
      (error: Response | any) => {
        next(error);
      }
    );
  }

  // Add mention to post
  private addNewPostMentions(state, next) {
    // Validate input
    if (!state.mention_id || !state.postData || !state.postData.url ||
          !state.postData.publishDate || !state.postData.relativePath || !state.postData.urlHash) {
      return next(new Error('Missing parameters for `addNewPostMentions()`.'));
    }

    state.influencerPostService.addNewPostMention(state.mention_id, state.postData.url, state.postData.publishDate,
      state.postData.relativepath, state.postData.urlHash, state.postData.influencerId).subscribe(
      // Success
      (data) => {
        if (!data) {
          return next(new Error('An error occurred while adding a mention to a post, `data` was undefined.'));
        }

        // track event to Segment
        state.eventTracker.trackUserAction(UserActionType.ACTION, Page.POST, {
          id: state.postUrlHash,
          name: state.postTitle,
          type: TargetType.POST
        }, {
          action: UserActionType.UPDATE,
          actionDetails: ActionDetails.ADDED_MENTION,
          source: ActionSource.PAGE,
          mention_id: state.mention_id,
          mention: state.mention,
          keywordType: state.keywordType
        }, undefined, {
          type: ActivePages.POST,
          name: state.postTitle,
          id: state.postUrlHash
        });

        next();
      },

      // Error
      (error: Response | any) => {
        next(error);
      }
    );
  }

  onLoadedPostImage() {
    this.pdfService.updateNumberImageLoaded(PostContentComponent.COMPONENT_NAME);
  }

  onHoverPostImage() {
    if (!this.postContent.isVideo) {
      return;
    }
    if (this.storyPreviewPopup) {
      this.zone.runOutsideAngular(() => {
        this.calculateStoryPreviewPosition(window.innerWidth, window.innerHeight);
        $('.overlay, .overlay-back').fadeIn(300);
        document.body.classList.add('no-scroll');
      });
    }
  }

  private calculateStoryPreviewPosition(windowWidth, windowHeight) {
    let storyWidth = 320;
    let storyHeight;
    if (this.postContent.isVideo) {
      storyHeight = (this.postContent.mediaHeight / this.postContent.mediaWidth) * storyWidth;
    } else {
      storyHeight = (this.postContent.imageHeight / this.postContent.imageWidth) * storyWidth;
    }
    const storyTop = (windowHeight - storyHeight - 70) / 2;
    if (storyTop < 75) {
      storyHeight = windowHeight - 2 * 75 - 70;
      if (this.postContent.isVideo) {
        storyWidth = (this.postContent.mediaWidth / this.postContent.mediaHeight) * storyHeight;
      } else {
        storyWidth = (this.postContent.imageWidth / this.postContent.imageHeight) * storyHeight;
      }
      this.storyPreviewPopup.nativeElement.style.top = '75px';
    } else {
      this.storyPreviewPopup.nativeElement.style.top = storyTop + 'px';
    }
    this.storyPreviewPopup.nativeElement.style.width = storyWidth + 'px';
    this.storyPreviewPopup.nativeElement.style.height = storyHeight + 'px';
    this.storyPreviewPopup.nativeElement.style.left = `calc(50% - ${storyWidth / 2}px)`;
  }

  onClickOutsideOverlay(evt) {
    this.closeStoryPreviewPopup();
  }

  closeStoryPreviewPopup() {
    if (!this.postContent.isVideo) {
      return;
    }
    if (this.storyPreviewPopup) {
      $('.overlay, .overlay-back').fadeOut(300);
      document.body.classList.remove('no-scroll');
      const videoPlayer = this.storyPreviewPopup.nativeElement.querySelector('video');
      // pause video when close popup
      if (videoPlayer) {
        videoPlayer.pause();
      }
    }
  }

  get allowToAddNewMention() {
    return this.showAddNewMention && !this.userService.isFreeUser();
  }

  onResize(event) {
    this.zone.runOutsideAngular(() => {
      this.calculateStoryPreviewPosition(event.target.innerWidth, event.target.innerHeight);
    });
  }

  ngAfterViewInit() {
    this.pdfService.imagedLoadedTrigger.subscribe(data => {
      if (data.component === PostContentComponent.COMPONENT_NAME && data.currentCounter === 1) {
        this.pdfService.incrementNumberReportReady(PostContentComponent.COMPONENT_NAME);
      }
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/main/post-overview/post-content/post-content.component.ts